import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAllGyms } from '../services/firebase/gym';
import '../styles/HamburgerMenu.css';
import MyIconButton from './ui/MyIconButton';

const HamburgerMenu: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [hasOwnerOrFreRole, setHasOwnerOrFreRole] = useState(false);
  const [hasMultipleGyms, setHasMultipleGyms] = useState(false);
  const [singleGymId, setSingleGymId] = useState<string | null>(null);
  const { currentUser, climberProfile } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkUserRoles = () => {
      if (!currentUser || !climberProfile) {
        setHasAdminAccess(false);
        setHasOwnerOrFreRole(false);
        return;
      }

      try {
        // Use climber profile from context
        if (!climberProfile.gymMemberships) {
          setHasAdminAccess(false);
          setHasOwnerOrFreRole(false);
          return;
        }

        // Check for 'fre' role (free role - giving admin access)
        const hasFreeRole = Object.values(climberProfile.gymMemberships).some(
          role => role === 'fre'
        );
        setHasAdminAccess(hasFreeRole);
        
        // Check for 'owner' or 'fre' role (giving gym management access)
        const gymEntries = Object.entries(climberProfile.gymMemberships);
        const ownerOrFreEntries = gymEntries.filter(
          ([_, role]) => role === 'owner' || role === 'fre'
        );
        
        const hasOwnerOrFre = ownerOrFreEntries.length > 0;
        setHasOwnerOrFreRole(hasOwnerOrFre);
        
        // Check if user has multiple gyms with owner/fre role
        if (hasOwnerOrFre) {
          setHasMultipleGyms(ownerOrFreEntries.length > 1);
          
          // If user has only one gym, store its ID
          if (ownerOrFreEntries.length === 1) {
            setSingleGymId(ownerOrFreEntries[0][0]);
          } else {
            setSingleGymId(null);
          }
        } else {
          setHasMultipleGyms(false);
          setSingleGymId(null);
        }

      } catch (err) {
        console.error('Error checking user roles:', err);
        setHasAdminAccess(false);
        setHasOwnerOrFreRole(false);
      }
    };

    checkUserRoles();
  }, [currentUser, climberProfile]);

  // Handle clicking outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleMenuItemClick = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="menu-container" ref={menuRef}>
      <MyIconButton onClick={toggleMenu} ariaLabel="Menu" className="hamburger-button-custom">
        <div className="hamburger-icon">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </MyIconButton>
      {menuOpen && (
        <div className="menu-dropdown">
          <ul className="menu-list">
            {hasAdminAccess && (
              <li onClick={() => handleMenuItemClick('/dashboard')}>Dashboard</li>
            )}
            {hasOwnerOrFreRole && (
              <li onClick={() => handleMenuItemClick(hasMultipleGyms ? '/my-gyms' : `/my-gyms/${singleGymId}/edit`)}>My gym{hasMultipleGyms ? 's' : ''}</li>
            )}
            <li onClick={() => handleMenuItemClick('/profile')}>My Profile</li>
            <li onClick={() => handleMenuItemClick('/hrm')}>Heart Rate Monitor</li>
            <li onClick={() => handleMenuItemClick('/join-gym')}>Join Gym</li>
            <li onClick={() => handleMenuItemClick('/about')}>About</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default HamburgerMenu;
